/** Вход, выбор сервера, настройки доступа. */
import { DEMO, api } from '../api.js';
import { app } from '../ctx.js';
import { clear, h, icon } from '../dom.js';
import { navigate } from '../router.js';
import { toast } from '../ui/overlay.js';
import { themeButton } from '../theme.js';
import { rolePicker } from '../ui/roles.js';

function brand() {
  return h('div', { class: 'brand' }, h('div', { class: 'brand-mark' }, icon('sparkles', 20)), h('div', null, h('div', { class: 'brand-name' }, 'Embed Studio'), h('div', { class: 'brand-sub' }, 'Embed и Components V2 для Discord')));
}

const LOGIN_ERRORS: Record<string, string> = {
  oauth: 'Вход через Discord не настроен на этом боте. Используйте команду /panel на сервере - она пришлёт личную ссылку.',
  state: 'Сессия входа устарела, попробуйте ещё раз.',
  token: 'Discord не подтвердил вход. Попробуйте ещё раз.',
  user: 'Не удалось получить профиль Discord.',
  magic: 'Ссылка устарела (действует 15 минут). Вызовите /panel ещё раз.',
};

export function loginPage(root: HTMLElement, query: URLSearchParams) {
  clear(root);
  const err = query.get('error');
  root.append(
    h(
      'div',
      { class: 'center-page' },
      h(
        'div',
        { class: 'login-card glass' },
        h('div', { class: 'login-top' }, brand(), themeButton()),
        h('h1', { class: 'login-title' }, 'Сообщения, которые хочется читать'),
        h('p', { class: 'muted' }, 'Эмбеды, Components V2, кнопки выдачи ролей и шаблоны сервера - с живым предпросмотром в стиле Discord.'),
        err ? h('div', { class: 'callout callout-error' }, icon('alert', 16), LOGIN_ERRORS[err] ?? 'Не удалось войти') : null,
        app.me?.oauth ? h('a', { class: 'btn btn-primary btn-lg btn-block', href: '/auth/login' }, icon('logout', 18), 'Войти через Discord') : null,
        h(
          'div',
          { class: 'login-alt' },
          h('div', { class: 'login-step' }, h('span', { class: 'step-num' }, '1'), 'Напишите на сервере ', h('code', null, '/panel')),
          h('div', { class: 'login-step' }, h('span', { class: 'step-num' }, '2'), 'Откройте личную ссылку от бота - вход произойдёт сам'),
          h('div', { class: 'login-step' }, h('span', { class: 'step-num' }, '3'), 'Или ПКМ по сообщению -> Приложения -> ', h('strong', null, 'Спарсить')),
        ),
        app.me?.inviteUrl ? h('a', { class: 'link small', href: app.me.inviteUrl, target: '_blank', rel: 'noreferrer' }, icon('plus', 14), ' Добавить бота на сервер') : null,
      ),
    ),
  );
}

export async function guildsPage(root: HTMLElement) {
  clear(root);
  const grid = h('div', { class: 'guild-grid' }, h('div', { class: 'skeleton' }), h('div', { class: 'skeleton' }), h('div', { class: 'skeleton' }));
  const me = app.me!.user!;
  root.append(
    h(
      'div',
      { class: 'page' },
      h(
        'header',
        { class: 'page-head glass' },
        brand(),
        h('div', { class: 'grow' }),
        themeButton(),
        h('div', { class: 'user-chip' }, h('img', { src: me.avatarUrl, alt: '' }), h('span', null, me.globalName || me.username)),
        DEMO
          ? null
          : h(
              'button',
              {
                class: 'icon-btn',
                type: 'button',
                title: 'Выйти',
                onclick: async () => {
                  await api.logout();
                  location.href = '/#/login';
                  location.reload();
                },
              },
              icon('logout', 18),
            ),
      ),
      h('h1', { class: 'page-title' }, 'Выберите сервер'),
      h('p', { class: 'muted' }, 'Показаны серверы, где есть бот и у вас есть доступ к редактору.'),
      grid,
    ),
  );
  try {
    const guilds = await api.guilds();
    grid.replaceChildren(
      ...guilds.map((g) =>
        h(
          'button',
          { class: 'guild-card glass', type: 'button', onclick: () => navigate(`#/g/${g.id}/editor`) },
          g.iconUrl ? h('img', { class: 'guild-icon', src: g.iconUrl, alt: '' }) : h('div', { class: 'guild-icon guild-fallback' }, g.name.slice(0, 2)),
          h('div', { class: 'guild-card-name' }, g.name),
          h('div', { class: 'muted small' }, g.canManage ? 'администратор' : 'доступ по роли'),
        ),
      ),
    );
    if (!guilds.length) {
      grid.replaceChildren(
        h(
          'div',
          { class: 'empty-state glass' },
          icon('info', 22),
          h('div', null, h('strong', null, 'Серверов нет'), h('p', { class: 'muted' }, 'Добавьте бота на сервер или попросите администратора выдать доступ командой /access add.')),
          app.me?.inviteUrl ? h('a', { class: 'btn btn-primary', href: app.me.inviteUrl, target: '_blank', rel: 'noreferrer' }, icon('plus', 16), 'Добавить бота') : null,
        ),
      );
    }
    let visited = false;
    try {
      visited = !!sessionStorage.getItem('es:visited');
      sessionStorage.setItem('es:visited', '1');
    } catch {
      /* хранилище недоступно */
    }
    if (guilds.length === 1 && !visited) navigate(`#/g/${guilds[0].id}/editor`);
  } catch (err: any) {
    grid.replaceChildren(h('div', { class: 'callout callout-error' }, icon('alert', 16), err.message));
  }
}

export function settingsPage(root: HTMLElement) {
  const g = app.guild!;
  clear(root);
  if (!g.settings) {
    root.append(h('div', { class: 'center-page' }, h('div', { class: 'callout callout-error' }, 'Нужно право «Управлять сервером»')));
    return;
  }
  const selected = new Set(g.settings.allowedRoles);
  const folderAdmins = new Set(g.settings.folderAdminRoles ?? []);
  let logChannel = g.settings.logChannelId;

  const logSel = h(
    'select',
    { class: 'input select', attrs: { 'aria-label': 'Канал логов' } },
    h('option', { value: '' }, 'Не вести логи'),
    g.channels.filter((c) => c.type === 0 || c.type === 5).map((c) => h('option', { value: c.id, selected: c.id === logChannel }, `# ${c.name}`)),
  );
  logSel.addEventListener('change', () => (logChannel = logSel.value || null));

  const saveBtn = h(
    'button',
    {
      class: 'btn btn-primary',
      type: 'button',
      onclick: async () => {
        saveBtn.disabled = true;
        try {
          const s = await api.saveSettings(g.id, { allowedRoles: [...selected], logChannelId: logChannel, folderAdminRoles: [...folderAdmins] });
          g.settings = s;
          toast('Настройки сохранены', 'ok');
        } catch (err: any) {
          toast(err.message, 'error');
        } finally {
          saveBtn.disabled = false;
        }
      },
    },
    icon('save', 16),
    'Сохранить',
  );

  root.append(
    h(
      'div',
      { class: 'page narrow' },
      h(
        'header',
        { class: 'page-head glass' },
        h('button', { class: 'btn btn-ghost', type: 'button', onclick: () => navigate(`#/g/${g.id}/editor`) }, icon('chevronRight', 16, 'flip'), 'К редактору'),
        h('div', { class: 'grow' }),
        h('strong', null, g.name),
        themeButton(),
      ),
      h('h1', { class: 'page-title' }, 'Доступ и настройки'),
      h(
        'section',
        { class: 'panel glass' },
        h('header', { class: 'panel-head static' }, h('span', { class: 'panel-icon' }, icon('shield', 18)), h('h3', { class: 'panel-title' }, 'Кто может пользоваться ботом')),
        h(
          'div',
          { class: 'panel-body' },
          h('p', { class: 'muted small' }, 'Владелец сервера и роли с правом «Управлять сервером» имеют доступ всегда. Отметьте роли, которым тоже можно открывать редактор, работать с шаблонами и пользоваться «Спарсить». Отправлять можно только в каналы, где у человека есть право писать.'),
          rolePicker(g.roles, selected),
        ),
      ),
      h(
        'section',
        { class: 'panel glass' },
        h('header', { class: 'panel-head static' }, h('span', { class: 'panel-icon' }, icon('folder', 18)), h('h3', { class: 'panel-title' }, 'Папки шаблонов')),
        h(
          'div',
          { class: 'panel-body' },
          h(
            'p',
            { class: 'muted small' },
            'Эти роли могут создавать, переименовывать и удалять папки шаблонов и настраивать, какие роли видят и редактируют каждую папку. Им открыты все папки. «Управлять сервером» может это всегда. Роли должны также иметь доступ к редактору (блок выше).',
          ),
          rolePicker(g.roles, folderAdmins),
        ),
      ),
      h(
        'section',
        { class: 'panel glass' },
        h('header', { class: 'panel-head static' }, h('span', { class: 'panel-icon' }, icon('file', 18)), h('h3', { class: 'panel-title' }, 'Журнал действий')),
        h('div', { class: 'panel-body' }, h('p', { class: 'muted small' }, 'Бот будет писать, кто и куда отправил или изменил сообщение, а также изменения шаблонов.'), h('label', { class: 'field' }, logSel)),
      ),
      h('div', { class: 'page-actions' }, saveBtn),
    ),
  );
}
